import { Plus } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { ErrorCard } from '@/components/ErrorCard'
import { PageContainer } from '@/components/PageContainer'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { PageHeader } from '@/features/preinvest/components/PageHeader'
import { Segmented } from '@/features/tasks/Segmented'
import { toastFailure } from '@/lib/feedback'
import { AgreementDialog } from '../agreements/AgreementDialog'
import type { AgreementParty } from '../agreements/types'
import { NewContactDialog } from '../NewContactDialog'
import { todayIso } from '../dates'
import { InsprekenCard } from '../inspreken/InsprekenCard'
import { ActivityWidget } from './ActivityWidget'
import { AgendaWidget } from './AgendaWidget'
import { getDashboard } from './api'
import { CategoryWidget } from './CategoryWidget'
import { longDay } from './dates'
import { KpiRow } from './KpiRow'
import { OverdueWidget } from './OverdueWidget'
import { RhythmWidget } from './RhythmWidget'
import { readScope, writeScope } from './scopeStore'
import type { Dashboard, Part, Scope } from './types'
import { useItemActions } from './useItemActions'
import { WithoutAgreementWidget } from './WithoutAgreementWidget'

const WEEKS = 4
/** After ticking or postponing only the widgets it can change are fetched again (design §4.1). */
const REFRESH_PARTS: readonly Part[] = ['kpis', 'agenda', 'overdue']
const SCOPES = [
  { value: 'mine', label: 'Mijn' },
  { value: 'team', label: 'Team' },
] as const

/** Minimum height of a widget card from md up, so a sparse card never leaves a gap in the mosaic. */
const FILL = 'md:min-h-60 md:*:flex-1'

/** A grid slot; the DOM order is the mobile order, `md:order-*` restores the desktop layout. `fill` makes its card stretch. */
function Slot({ className, fill = false, children }: { className: string; fill?: boolean; children: ReactNode }) {
  return (
    <div data-overzicht-slot className={`flex min-w-0 flex-col ${fill ? FILL : ''} ${className}`}>
      {children}
    </div>
  )
}

function WidgetSkeleton({ label, className, fill = true }: { label: string; className: string; fill?: boolean }) {
  return (
    <Slot className={className} fill={fill}>
      <Skeleton aria-label={`${label} wordt geladen`} className={fill ? 'h-full min-h-48 w-full rounded-3xl' : 'h-28 w-full rounded-3xl'} />
    </Slot>
  )
}

/**
 * Overzicht (spec-63 E5, design §4.1, ED-44): key figures, agenda, overdue, contact rhythm, categories,
 * activity and relations without agreement, for Mijn or Team. One request fills the page;
 * after an action only kpis, agenda and overdue are fetched again and merged.
 */
export function OverzichtPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<Dashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [weekOnly, setWeekOnly] = useState(false)
  const [contactOpen, setContactOpen] = useState(false)
  const [agreement, setAgreement] = useState<{ party: AgreementParty | null } | null>(null)
  const ticket = useRef(0)
  const partialTicket = useRef(0)

  /** Fetches the whole Overzicht; the state only changes once the answer is in. */
  const fetchAll = useCallback(async (scope: Scope | undefined): Promise<boolean> => {
    const mine = ++ticket.current
    try {
      const answer = await getDashboard({ scope, weeks: WEEKS })
      if (mine !== ticket.current) return false
      setData(answer)
      return true
    } catch (caught) {
      if (mine !== ticket.current) return false
      setFailed(true)
      toastFailure(caught, 'Overzicht laden mislukt.')
      return false
    } finally {
      if (mine === ticket.current) setLoading(false)
    }
  }, [])

  const load = useCallback(
    (scope: Scope | undefined) => {
      setLoading(true)
      setFailed(false)
      return fetchAll(scope)
    },
    [fetchAll],
  )

  useEffect(() => {
    // A first visit sends no scope: the server picks Mijn or Team and the page adopts it.
    const mine = ++ticket.current
    async function first() {
      try {
        const answer = await getDashboard({ scope: readScope() ?? undefined, weeks: WEEKS })
        if (mine === ticket.current) setData(answer)
      } catch (caught) {
        if (mine !== ticket.current) return
        setFailed(true)
        toastFailure(caught, 'Overzicht laden mislukt.')
      } finally {
        if (mine === ticket.current) setLoading(false)
      }
    }
    void first()
  }, [])

  const scope: Scope = data?.scope ?? 'team'

  // A partial refresh has its own ticket so it never cancels a full load; a full load that started
  // meanwhile (scope switch) makes the partial answer stale, so it is dropped.
  const refresh = useCallback(async () => {
    const mine = ++partialTicket.current
    const full = ticket.current
    try {
      const answer = await getDashboard({ scope, weeks: WEEKS, parts: REFRESH_PARTS })
      if (mine !== partialTicket.current || full !== ticket.current) return
      setData((current) =>
        current
          ? { ...current, today: answer.today, kpis: answer.kpis, agenda: answer.agenda, more: answer.more, overdue: answer.overdue }
          : current,
      )
    } catch (caught) {
      if (mine === partialTicket.current && full === ticket.current) toastFailure(caught, 'Overzicht verversen mislukt.')
    }
  }, [scope])

  const today = data?.today ?? todayIso()
  const actions = useItemActions(today, () => void refresh())

  async function chooseScope(next: Scope) {
    if (next === scope && data) return
    const previous = scope
    const hadData = data !== null
    writeScope(next)
    const ok = await load(next)
    // The shown data still belongs to the old scope: keep the stored choice in step with it.
    if (!ok && hadData) writeScope(previous)
  }

  function showWeek() {
    setWeekOnly(true)
    document.getElementById('agenda')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }

  const header = (
    <PageHeader
      title="Overzicht"
      subtitle={`Relatiebeheer · ${longDay(today)}`}
      actions={
        <>
          <Segmented label="Weergave" value={scope} options={SCOPES} onChange={(next) => void chooseScope(next)} />
          <Button type="button" variant="outline" onClick={() => setContactOpen(true)}>
            <Plus aria-hidden />
            Contact
          </Button>
          <Button type="button" onClick={() => setAgreement({ party: null })}>
            <Plus aria-hidden />
            Afspraak
          </Button>
        </>
      }
    />
  )

  const dialogs = (
    <>
      <NewContactDialog open={contactOpen} onOpenChange={setContactOpen} onCreated={(personId) => navigate(`/relaties/contacten/${personId}`)} />
      {agreement ? (
        <AgreementDialog
          key={agreement.party ? `${agreement.party.kind}:${agreement.party.id}` : 'nieuw'}
          open
          onOpenChange={(open) => {
            if (!open) setAgreement(null)
          }}
          party={agreement.party}
          onSaved={() => {
            setAgreement(null)
            void refresh()
          }}
        />
      ) : null}
      {actions.dialogs}
    </>
  )

  if (data === null && failed) {
    return (
      <PageContainer className="flex flex-col gap-4">
        {header}
        <ErrorCard title="Het Overzicht kon niet worden geladen" onRetry={() => void load(readScope() ?? undefined)} />
        {dialogs}
      </PageContainer>
    )
  }

  const newAgreement = () => setAgreement({ party: null })
  return (
    <PageContainer className="flex flex-col gap-4">
      {header}
      <div aria-busy={loading} className="grid grid-cols-1 gap-4 md:grid-cols-12">
        {/* spec-63 E7: on a phone the way in to Inspreken is the first slot (the card hides itself from md up). */}
        <Slot className="md:hidden">
          <InsprekenCard />
        </Slot>
        {data?.kpis ? (
          <Slot className="md:order-1 md:col-span-12">
            <KpiRow kpis={data.kpis} scope={scope} onShowWeek={showWeek} />
          </Slot>
        ) : (
          <WidgetSkeleton label="Kerncijfers" className="md:order-1 md:col-span-12" fill={false} />
        )}
        {data?.overdue ? (
          <Slot fill className="md:order-3 md:col-span-4">
            <OverdueWidget items={data.overdue} today={today} actions={actions} />
          </Slot>
        ) : (
          <WidgetSkeleton label="Achterstallig" className="md:order-3 md:col-span-4" />
        )}
        {data?.agenda ? (
          <Slot fill className="md:order-2 md:col-span-8 md:row-span-2">
            <AgendaWidget
              items={data.agenda}
              more={data.more}
              weeks={WEEKS}
              today={today}
              scope={scope}
              weekOnly={weekOnly}
              onWeekOnlyChange={setWeekOnly}
              actions={actions}
              onNewAgreement={newAgreement}
            />
          </Slot>
        ) : (
          <WidgetSkeleton label="Agenda" className="md:order-2 md:col-span-8 md:row-span-2" />
        )}
        {data?.rhythm ? (
          <Slot fill className="md:order-4 md:col-span-4">
            <RhythmWidget rhythm={data.rhythm} scope={scope} />
          </Slot>
        ) : (
          <WidgetSkeleton label="Contactritme" className="md:order-4 md:col-span-4" />
        )}
        {data?.without_agreement ? (
          <Slot fill className="md:order-7 md:col-span-4">
            <WithoutAgreementWidget
              rows={data.without_agreement}
              total={data.without_agreement_total}
              onNewAgreement={(party) => setAgreement({ party })}
            />
          </Slot>
        ) : (
          <WidgetSkeleton label="Zonder afspraak" className="md:order-7 md:col-span-4" />
        )}
        {data?.by_category ? (
          <Slot fill className="md:order-5 md:col-span-4">
            <CategoryWidget rows={data.by_category} scope={scope} />
          </Slot>
        ) : (
          <WidgetSkeleton label="Relaties per categorie" className="md:order-5 md:col-span-4" />
        )}
        {data?.activity_weeks ? (
          <Slot fill className="md:order-6 md:col-span-4">
            <ActivityWidget weeks={data.activity_weeks} trend={data.activity} />
          </Slot>
        ) : (
          <WidgetSkeleton label="Activiteit" className="md:order-6 md:col-span-4" />
        )}
      </div>
      {dialogs}
    </PageContainer>
  )
}
